'use client'

import type { CreateSessionResponse } from '@/lib/compiler/types'
import { apiFetch, bffFetch } from '@/lib/api-client'

export type { ApiError } from '@/lib/api-client'
export { ApiRequestError } from '@/lib/api-client'

/** Saves a blob to the browser's default downloads location via a throwaway <a download> click. */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(objectUrl)
}

export interface LoginResponse {
  user: string
}

export interface SignupResponse extends LoginResponse {
  email?: string
}

export interface UserProfile {
  id: string
  email: string
  first_name: string
  last_name: string
  middle_name?: string
  username?: string
  phone_number?: string
  avatar_url?: string
  profile_url?: string
  profile_image_url?: string
  is_email_verified: boolean
  is_mfa_enabled: boolean
  is_phone_verified: boolean
  totp_enabled: boolean
  created_at: string
  updated_at: string
}

export interface LoginRequest {
  email: string
  password: string
}

export interface LoginTotpRequest {
  email: string
  password: string
  code: string
}

export interface SignupRequest {
  email: string
  first_name: string
  last_name: string
  middle_name: string
  password: string
  username: string
}

export interface TotpEnableResponse {
  message: string
  qr_code: string
  secret: string
}

export interface TotpVerifyResponse {
  enabled: boolean
  message: string
}

export interface TotpStatusResponse {
  enabled: boolean
}

export interface VerifyEmailPhoneRequest {
  otp: string
  email?: true
  phone?: true
}

export interface VerifyEmailPhoneResponse {
  message?: string
  [key: string]: unknown
}

export interface CreateProjectRequest {
  description: string
  name: string
  visibility: string
  tags: string[]
}

export interface CreateProjectData {
  id: string
  owner_id: string
  owner_name?: string
  name: string
  description?: string
  visibility: string
  type: "file" | "project" | "folder"
  extension?: string
  compiler?: string
  folder_count: number
  file_count: number
  total_size: number
  is_deleted: boolean
  is_pinned?: boolean
  tags?: string[]
  thumbnail_url?: string
  created_at: string
  updated_at: string
}

export interface CreateProjectResponse {
  success: boolean
  message: string
  data: CreateProjectData
}

/** GET /projects/:id's own response shape — richer than, and not identical to, CreateProjectData
 * (e.g. `type` comes back as a concrete label like "latex" here, not "project" + a compiler field). */
export interface ProjectDetail {
  id: string
  owner_id: string
  owner_name?: string
  name: string
  description?: string
  visibility: string
  tags?: string[]
  type: string
  extension?: string
  folder_count: number
  file_count: number
  total_size: number
  thumbnail_key?: string
  compiler?: string
  output_type?: string
  is_pinned?: boolean
  is_deleted?: boolean
  is_shared?: boolean
  public_read_url?: string
  created_at: string
  updated_at: string
}

export interface GetProjectByIdResponse {
  success: boolean
  message: string
  data: ProjectDetail
}

export interface CreateLatexTemplateRequest {
  compiler: string
  description: string
  editors: string[]
  name: string
  output_type: string
  readers: string[]
  tags: string[]
  thumbnail_key: string
  visibility: string
  project_id?: string
  parent_id?: string
}

export interface CreateLatexTemplateResponse {
  success: boolean
  message: string
  data: CreateProjectData
}

export interface CreateProjectSessionResponse {
  success: boolean
  message: string
  data?: {
    session_token: string
    compiler_endpoint: string
    expires_in: number
  }
}

export interface GetProjectsResponse {
  success: boolean
  message: string
  data: CreateProjectData[]
}

export interface UserSearchItem {
  user_id: string
  name: string
  email: string
}

export interface UserSearchResponse {
  success: boolean
  message: string
  data: {
    items: UserSearchItem[]
  }
}

export interface AdvancedSearchRequest {
  name?: string
  created_from?: string
  created_to?: string
  updated_from?: string
  updated_to?: string
  resource_type?: string
  tags?: string[]
  size_min?: number
  size_max?: number
  public_link?: boolean
  owner_id?: string
  scope?: string
  project_id?: string
  page?: number
  page_size?: number
}

export interface AdvancedSearchResponse {
  success: boolean
  message: string
  data: {
    home: {
      items: CreateProjectData[]
      total: number
      page: number
      page_size: number
    }
    shared: {
      items: CreateProjectData[]
      total: number
      page: number
      page_size: number
    }
    trash: {
      items: CreateProjectData[]
      total: number
      page: number
      page_size: number
    }
  }
}

class ApiService {
  async signup(userData: SignupRequest): Promise<SignupResponse> {
    return apiFetch<SignupResponse>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return apiFetch<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  async loginWithTotp(credentials: LoginTotpRequest): Promise<LoginResponse> {
    return apiFetch<LoginResponse>('/auth/login-totp', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  async getProfile(): Promise<UserProfile> {
    return apiFetch<UserProfile>('/auth/profile', { method: 'GET' })
  }

  async totpStatus(): Promise<TotpStatusResponse> {
    return apiFetch<TotpStatusResponse>('/totp/status', {
      method: 'GET',
      skipErrorToast: true,
    })
  }

  async enableTotp(password: string): Promise<TotpEnableResponse> {
    return apiFetch<TotpEnableResponse>('/totp/enable', {
      method: 'POST',
      body: JSON.stringify({ password }),
    })
  }

  async verifyTotp(code: string): Promise<TotpVerifyResponse> {
    return apiFetch<TotpVerifyResponse>('/totp/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    })
  }

  async verifyEmailOtp(otp: string): Promise<VerifyEmailPhoneResponse> {
    return apiFetch<VerifyEmailPhoneResponse>('/auth/verify-email-phone', {
      method: 'POST',
      body: JSON.stringify({ email: true, otp }),
    })
  }

  async verifyPhoneOtp(otp: string): Promise<VerifyEmailPhoneResponse> {
    return apiFetch<VerifyEmailPhoneResponse>('/auth/verify-email-phone', {
      method: 'POST',
      body: JSON.stringify({ phone: true, otp }),
    })
  }

  async createProject(data: CreateProjectRequest): Promise<CreateProjectResponse> {
    return apiFetch<CreateProjectResponse>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getImageUploadUrl(fileName: string, mimeType: string, type: "profile" | "thumbnail"): Promise<{ success: boolean; message: string; data: { upload_url: string; public_url: string; key: string; expires_in: number } }> {
    return apiFetch('/storage/images/upload-url', {
      method: 'POST',
      body: JSON.stringify({ file_name: fileName, mime_type: mimeType, type }),
    })
  }

  async createLatexTemplate(data: CreateLatexTemplateRequest): Promise<CreateLatexTemplateResponse> {
    return apiFetch<CreateLatexTemplateResponse>('/projects/latex-template', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async createProjectSession(
    projectId: string,
    options?: { skipErrorToast?: boolean; signal?: AbortSignal },
  ): Promise<CreateProjectSessionResponse> {
    return apiFetch<CreateProjectSessionResponse>(
      `/projects/${encodeURIComponent(projectId)}/session`,
      {
        method: 'POST',
        skipErrorToast: options?.skipErrorToast,
        signal: options?.signal,
      },
    )
  }

  async createCompilerSessionFromToken(
    sessionToken: string,
    options?: { skipErrorToast?: boolean; signal?: AbortSignal },
  ): Promise<CreateSessionResponse> {
    return apiFetch<CreateSessionResponse>('/compiler/internal/session/create', {
      method: 'POST',
      body: JSON.stringify({ session_token: sessionToken }),
      skipErrorToast: options?.skipErrorToast,
      signal: options?.signal,
    })
  }

  async getProjects(): Promise<GetProjectsResponse> {
    return apiFetch<GetProjectsResponse>('/projects', {
      method: 'GET',
    })
  }

  /** GET /projects/:id — used to resolve a /drive/privateurl/{uuid}/... link for a logged-in
   * user; skipErrorToast so a 403/404 there can show its own message instead of a generic toast. */
  async getProjectById(id: string): Promise<GetProjectByIdResponse> {
    return apiFetch<GetProjectByIdResponse>(`/projects/${encodeURIComponent(id)}`, {
      method: 'GET',
      skipErrorToast: true,
    })
  }

  async getSharedWithMe(): Promise<SharedWithMeResponse> {
    return apiFetch<SharedWithMeResponse>('/share/me', { method: 'GET', skipErrorToast: true })
  }

  async shareResource(resourceId: string, data: ShareResourceRequest): Promise<ShareResourceResponse> {
    return apiFetch<ShareResourceResponse>(`/share/${encodeURIComponent(resourceId)}`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async removeShare(resourceId: string, sharedWith: string): Promise<{ success: boolean; message: string }> {
    return apiFetch('/share', {
      method: 'DELETE',
      body: JSON.stringify({ resource_id: resourceId, shared_with: sharedWith }),
      skipErrorToast: true,
    })
  }

  async getWalletHistory(params: WalletHistoryParams = {}): Promise<WalletHistoryResponse> {
    const query = new URLSearchParams()
    if (params.limit != null) query.set('limit', String(params.limit))
    if (params.offset != null) query.set('offset', String(params.offset))
    const qs = query.toString()
    return apiFetch<WalletHistoryResponse>(`/wallet/history${qs ? `?${qs}` : ''}`, { method: 'GET' })
  }


  async getResourceAccess(resourceId: string): Promise<ResourceAccessResponse> {
    return apiFetch<ResourceAccessResponse>(`/share/${encodeURIComponent(resourceId)}`, {
      method: 'GET',
      skipErrorToast: true,
    })
  }

  async generatePublicLink(resourceId: string, options?: { role?: "read" | "editor" }): Promise<GeneratePublicLinkResponse> {
    const requestBody = options?.role ? { role: options.role } : undefined
    return apiFetch<GeneratePublicLinkResponse>(`/share/${encodeURIComponent(resourceId)}/public-link`, {
      method: 'POST',
      body: requestBody ? JSON.stringify(requestBody) : undefined,
    })
  }

  async deletePublicLink(
      resourceId: string,
      data?: { role?: "read" | "editor" }
  ): Promise<{ success: boolean; message: string }> {
    return apiFetch(`/share/${encodeURIComponent(resourceId)}/link`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data ?? {}),
    })
  }

  async getPublicShare(token: string): Promise<PublicShareResponse> {
    return apiFetch<PublicShareResponse>(`/share/public?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      skipErrorToast: true,
    })
  }

  async getPublicPreviewUrl(token: string, fileId: string): Promise<PublicPreviewResponse> {
    return apiFetch<PublicPreviewResponse>(`/share/public?token=${encodeURIComponent(token)}&fileId=${encodeURIComponent(fileId)}`, {
      method: 'GET',
      skipErrorToast: true,
    })
  }

  async addPublicShortcut(token: string): Promise<{ success: boolean; message: string; data?: any }> {
    return apiFetch(`/share/public/${encodeURIComponent(token)}/shortcut`, {
      method: "POST",
    })
  }

  async removePublicShortcut(resourceId: string): Promise<{ success: boolean; message: string; data?: any }> {
    return apiFetch(`/share/shortcut/${encodeURIComponent(resourceId)}`, {
      method: "DELETE",
    })
  }

  /** Soft-delete one or more shared files/folders (requires shared editor access) — POST /share/files/delete. */
  async deleteSharedFiles(fileIds: string[]): Promise<{ success: boolean; message: string }> {
    return apiFetch('/share/files/delete', {
      method: 'POST',
      body: JSON.stringify({ file_ids: fileIds }),
    })
  }

  async deleteProjects(projectIds: string[]): Promise<{ success: boolean; message: string }> {
    return apiFetch('/projects', {
      method: 'DELETE',
      body: JSON.stringify({ project_ids: projectIds }),
    })
  }

  /** Bulk soft-delete files/folders in one call — DELETE /files with { file_ids }. */
  async bulkSoftDeleteFiles(fileIds: string[]): Promise<{ success: boolean; message: string }> {
    return apiFetch('/files', {
      method: 'DELETE',
      body: JSON.stringify({ file_ids: fileIds }),
    })
  }

async createFile(payload: CreateFilePayload): Promise<{ success: boolean; message: string; data?: any }> {
  return apiFetch('/files', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

  async renameResource(
      id: string,
      payload: RenameResourcePayload
  ): Promise<{ success: boolean; message: string }> {
    return apiFetch(`/files/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: payload.name,
        extension: payload.extension ?? "",
        mime_type: payload.mime_type ?? "application/octet-stream",
      }),
    })
  }

  async updateProject(id: string, name: string): Promise<{ success: boolean; message: string }> {
    return apiFetch(`/projects/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    })
  }

  /** PUT /projects/:id with only the `tags` key — e.g. removing one tag from the Properties panel. */
  async updateProjectTags(id: string, tags: string[]): Promise<{ success: boolean; message: string }> {
    return apiFetch(`/projects/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ tags }),
    })
  }

  async restoreTrashItem(itemId: string, kind: "file" | "folder" | "project"): Promise<{ success: boolean; message: string }> {
    const body = {
      file_ids:    kind === "file"    ? [itemId] : [],
      folder_ids:  kind === "folder"  ? [itemId] : [],
      project_ids: kind === "project" ? [itemId] : [],
    }
    return apiFetch('/files/restore', { method: 'POST', body: JSON.stringify(body), skipErrorToast: true })
  }

  async restoreTrashItems(items: Array<{ id: string; kind: "file" | "folder" | "project" }>): Promise<{ success: boolean; message: string }> {
    const body = {
      file_ids:    items.filter(i => i.kind === "file").map(i => i.id),
      folder_ids:  items.filter(i => i.kind === "folder").map(i => i.id),
      project_ids: items.filter(i => i.kind === "project").map(i => i.id),
    }
    return apiFetch('/files/restore', { method: 'POST', body: JSON.stringify(body), skipErrorToast: true })
  }

  async deleteTrashItem(itemId: string, kind: "file" | "folder" | "project"): Promise<{ success: boolean; message: string }> {
    const body = {
      file_ids:    kind === "file"    ? [itemId] : [],
      folder_ids:  kind === "folder"  ? [itemId] : [],
      project_ids: kind === "project" ? [itemId] : [],
    }
    return apiFetch('/trash/empty', { method: 'POST', body: JSON.stringify(body), skipErrorToast: true })
  }

  async emptyTrash(fileIds: string[], folderIds: string[], projectIds: string[]): Promise<{ success: boolean; message: string }> {
    return apiFetch('/trash/empty', {
      method: 'POST',
      body: JSON.stringify({ file_ids: fileIds, folder_ids: folderIds, project_ids: projectIds }),
      skipErrorToast: true,
    })
  }

  async exportFolder(folderId: string): Promise<void> {
    const response = await bffFetch(`/api/folders/${encodeURIComponent(folderId)}/export`, { method: 'GET' })
    if (!response.ok) throw new Error('Export failed')

    const blob = await response.blob()
    const disposition = response.headers.get('content-disposition')
    const filenameMatch = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
    triggerBrowserDownload(blob, filenameMatch?.[1]?.replace(/['"]/g, '') ?? 'export.zip')
  }

  async createDownloadSession(data: CreateDownloadSessionRequest): Promise<CreateDownloadSessionResponse> {
    return apiFetch<CreateDownloadSessionResponse>('/downloads/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getDownloadPartUrl(data: DownloadPartUrlRequest): Promise<DownloadPartUrlResponse> {
    return apiFetch<DownloadPartUrlResponse>('/downloads/sessions/parts/url', {
      method: 'POST',
      body: JSON.stringify(data),
      skipErrorToast: true,
    })
  }

  async completeDownloadPart(data: CompleteDownloadPartRequest): Promise<void> {
    await apiFetch('/downloads/sessions/parts/complete', {
      method: 'POST',
      body: JSON.stringify(data),
      skipErrorToast: true,
    })
  }

  /** Resume an existing download session by file fingerprint (resource_id) — mirrors
   *  resumeUploadSession. No device_id sent, same call as the upload resume endpoint. */
  async resumeDownloadSession(data: ResumeDownloadSessionRequest): Promise<DownloadSessionStatusResponse> {
    return apiFetch<DownloadSessionStatusResponse>('/downloads/sessions/resume', {
      method: 'POST',
      body: JSON.stringify(data),
      skipErrorToast: true,
    })
  }

  /** Current progress/status of a resumable download session — same shape as resumeDownloadSession's response. */
  async getDownloadSessionStatus(downloadSessionId: string): Promise<DownloadSessionStatusResponse> {
    return apiFetch<DownloadSessionStatusResponse>(`/downloads/sessions/${encodeURIComponent(downloadSessionId)}`, {
      method: 'GET',
      skipErrorToast: true,
    })
  }

  /** Aborts a resumable download session server-side — used by Stop, not by an ordinary pause. */
  async abortDownloadSession(downloadSessionId: string): Promise<{ success: boolean; message: string }> {
    return apiFetch(`/downloads/sessions/${encodeURIComponent(downloadSessionId)}`, {
      method: 'DELETE',
      skipErrorToast: true,
    })
  }

  async completeDownloadSession(data: CompleteDownloadSessionRequest): Promise<void> {
    await apiFetch('/downloads/sessions/complete', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getFilePreview(fileId: string): Promise<FilePreviewResponse> {
    const res = await apiFetch<FilePreviewResponse & { data?: FilePreviewData & { url?: string } }>(
      `/files/${encodeURIComponent(fileId)}/preview`,
      { method: 'GET', skipErrorToast: true },
    )
    if (res.data) {
      const signedUrl = res.data.preview_url || res.data.url
      if (signedUrl) res.data.preview_url = signedUrl
    }
    return res
  }

  async getFileTree(projectId: string): Promise<FileTreeResponse> {
    return apiFetch<FileTreeResponse>(
      `/files/tree?project_id=${encodeURIComponent(projectId)}`,
      { method: 'GET' }
    )
  }

  async getTrash(): Promise<TrashResponse> {
    return apiFetch<TrashResponse>('/trash', { method: 'GET', skipErrorToast: true })
  }

  async getPins(): Promise<PinsResponse> {
    return apiFetch<PinsResponse>('/pins', { method: 'GET', skipErrorToast: true })
  }

  async getWalletBalance(): Promise<WalletBalanceResponse> {
    return apiFetch<WalletBalanceResponse>('/wallet/balance', { method: 'GET' })
  }

  async pinResource(resourceId: string): Promise<{ success: boolean; message: string; data: string }> {
    return apiFetch('/pins', {
      method: 'POST',
      body: JSON.stringify({ resource_id: resourceId }),
      skipErrorToast: true,
    })
  }

  async deletePin(resourceId: string): Promise<{ success: boolean; message: string; data: string }> {
    return apiFetch(`/pins/${encodeURIComponent(resourceId)}`, { method: 'DELETE', skipErrorToast: true })
  }

  async initUploadSession(data: InitUploadSessionRequest): Promise<InitUploadSessionResponse> {
    return apiFetch<InitUploadSessionResponse>('/uploads/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** Look up an existing upload session by file fingerprint (name/size/mime/parent/project), with its uploaded_parts. */
  async resumeUploadSession(data: InitUploadSessionRequest): Promise<UploadSessionStatusResponse> {
    return apiFetch<UploadSessionStatusResponse>('/uploads/sessions/resume', {
      method: 'POST',
      body: JSON.stringify(data),
      skipErrorToast: true,
    })
  }

  async verifyUploadSessionHash(data: VerifyUploadSessionHashRequest): Promise<VerifyUploadSessionHashResponse> {
    return apiFetch<VerifyUploadSessionHashResponse>('/uploads/sessions/verify', {
      method: 'POST',
      body: JSON.stringify(data),
      skipErrorToast: true,
    })
  }



  async getUploadPartUrl(data: UploadPartUrlRequest): Promise<UploadPartUrlResponse> {
    return apiFetch<UploadPartUrlResponse>('/uploads/sessions/parts/url', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async completeUploadSession(data: CompleteUploadSessionRequest): Promise<CompleteUploadSessionResponse> {
    return apiFetch<CompleteUploadSessionResponse>('/uploads/sessions/complete', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async abortUploadSession(sessionId: string): Promise<void> {
    await apiFetch(`/uploads/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
      skipErrorToast: true,
    })
  }

  async getUploadSessionStatus(sessionId: string): Promise<UploadSessionStatusResponse> {
    return apiFetch<UploadSessionStatusResponse>(`/uploads/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'GET',
      skipErrorToast: true,
    })
  }

  /* Normalizes whatever envelope the backend wraps the list in — a bare array, { items }, { data },
     or { data: { items } } — since that has drifted before and callers just want the array. */
  async listPausedUploadSessions(): Promise<PausedUploadSessionSummary[]> {
    const resp = await apiFetch<unknown>('/uploads/sessions/paused', {
      method: 'GET',
      skipErrorToast: true,
    })
    if (Array.isArray(resp)) return resp as PausedUploadSessionSummary[]
    if (resp && typeof resp === 'object') {
      const { items, data } = resp as { items?: unknown; data?: unknown }
      if (Array.isArray(items)) return items as PausedUploadSessionSummary[]
      if (Array.isArray(data)) return data as PausedUploadSessionSummary[]
      if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
        return (data as { items: unknown[] }).items as PausedUploadSessionSummary[]
      }
    }
    return []
  }

  async createFolder(data: CreateFolderRequest): Promise<CreateFolderResponse> {
    return apiFetch<CreateFolderResponse>('/files/folder', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async createTextFile(data: CreateTextFileRequest): Promise<CreateTextFileResponse> {
    return apiFetch<CreateTextFileResponse>('/files/text-file', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async checkDuplicate(data: CheckDuplicateRequest): Promise<CheckDuplicateResponse> {
    return apiFetch<CheckDuplicateResponse>('/files/check-duplicate', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async copyResources(data: CopyResourceRequest): Promise<CopyResourceResponse> {
    return apiFetch<CopyResourceResponse>('/files/copy', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getCopyConflicts(operationId: string): Promise<MoveConflictsResponse> {
    return apiFetch<MoveConflictsResponse>(`/files/operations/${encodeURIComponent(operationId)}/conflicts`, {
      method: 'GET',
    })
  }

  async resolveCopyConflicts(operationId: string, data: CopyResolveRequest): Promise<MoveResolveResponse> {
    return apiFetch<MoveResolveResponse>(`/files/copy/${encodeURIComponent(operationId)}/resolve`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async moveResource(resourceId: string, data: MoveResourceRequest): Promise<MoveResourceResponse> {
    return apiFetch<MoveResourceResponse>(`/files/${encodeURIComponent(resourceId)}/move`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async getMoveConflicts(operationId: string): Promise<MoveConflictsResponse> {
    return apiFetch<MoveConflictsResponse>(`/files/operations/${encodeURIComponent(operationId)}/conflicts`, {
      method: 'GET',
    })
  }

  async resolveMoveConflicts(operationId: string, data: MoveResolveRequest): Promise<MoveResolveResponse> {
    return apiFetch<MoveResolveResponse>(`/files/move/${encodeURIComponent(operationId)}/resolve`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async searchFolders(params: { q: string; page?: number; page_size?: number }): Promise<SearchFoldersResponse> {
    const query = new URLSearchParams({
      q: params.q,
      ...(params.page && { page: String(params.page) }),
      ...(params.page_size && { page_size: String(params.page_size) }),
    })
    return apiFetch<SearchFoldersResponse>(`/folders/search?${query.toString()}`, {
      method: 'GET',
    })
  }

  async searchUsers(query: string, limit = 20): Promise<UserSearchResponse> {
    const params = new URLSearchParams({
      query,
      limit: String(limit),
    })
    return apiFetch<UserSearchResponse>(`/users/search?${params.toString()}`, {
      method: 'GET',
      skipErrorToast: true,
    })
  }

  async advancedSearch(filters: AdvancedSearchRequest): Promise<AdvancedSearchResponse> {
    const params = new URLSearchParams()
    if (filters.name) params.append('name', filters.name)
    if (filters.created_from) params.append('created_from', filters.created_from)
    if (filters.created_to) params.append('created_to', filters.created_to)
    if (filters.updated_from) params.append('updated_from', filters.updated_from)
    if (filters.updated_to) params.append('updated_to', filters.updated_to)
    if (filters.resource_type) params.append('resource_type', filters.resource_type)
    if (filters.tags && filters.tags.length > 0) {
      filters.tags.forEach(tag => params.append('tags', tag))
    }
    if (filters.size_min !== undefined) params.append('size_min', String(filters.size_min))
    if (filters.size_max !== undefined) params.append('size_max', String(filters.size_max))
    if (filters.public_link !== undefined) params.append('public_link', String(filters.public_link))
    if (filters.owner_id) params.append('owner_id', filters.owner_id)
    if (filters.scope) params.append('scope', filters.scope)
    if (filters.project_id) params.append('project_id', filters.project_id)
    params.append('page', String(filters.page ?? 1))
    params.append('page_size', String(filters.page_size ?? 20))

    return apiFetch<AdvancedSearchResponse>(`/files/advanced-search?${params.toString()}`, {
      method: 'GET',
      skipErrorToast: true,
    })
  }

  async logout(): Promise<void> {
    await apiFetch('/auth/logout', { method: 'POST', skipErrorToast: true })
  }

  async getUploadJobs(
      page = 1,
      pageSize = 50,
      type: "omit" | "upload" | "download" = "omit"
  ): Promise<UploadJobsResponse> {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      type: type || "omit",
    })

    return apiFetch<UploadJobsResponse>(`/uploads/jobs?${params.toString()}`, {
      method: "GET",
      skipErrorToast: true,
    })
  }

  async getUserPreferences(): Promise<UserPreferencesResponse> {
    return apiFetch<UserPreferencesResponse>('/users/settings/preferences', {
      method: 'GET',
      skipErrorToast: true,
    })
  }

  async updateUserPreferences(data: UpdateUserPreferencesRequest): Promise<UserPreferencesResponse> {
    return apiFetch<UserPreferencesResponse>('/users/settings/preferences', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }
}



export interface ResourceAccessItem {
  user_id: string
  email: string
  name: string
  role: "read" | "editor"
}

export interface ResourceAccessResponse {
  success: boolean
  message: string
  data: {
    resource_id: string
    items: ResourceAccessItem[]
    public_read_link?: {
      resource_id: string
      token: string
      permission: "read"
      path: string
    }
    public_edit_link?: {
      resource_id: string
      token: string
      permission: "editor"
      path: string
    }
  }
}

export interface GeneratePublicLinkResponse {
  success: boolean
  message: string
  data: {
    resource_id: string
    token: string
    permission: "read" | "editor"
    path: string
    /** Absolute URL on this app (`/share/public/:token`), added by the BFF route. */
    public_url?: string
  }
}

export interface PublicShareResponse {
  success: boolean
  message: string
  data: {
    resource_id: string
    resource_type: string
    project_id?: string
    name: string
    editable: boolean
    tree: FileTreeNode[]
  }
}

export interface PublicPreviewResponse {
  success: boolean
  message: string
  data: string
}

export interface ShareResourceRequest {
  role: "read" | "editor"
  shared_with: string[]
}

export interface ShareResourceResponse {
  success: boolean
  message: string
}

export interface SharedResource {
  id: string
  project_id?: string
  parent_id?: string
  owner_id: string
  owner_name?: string
  name: string
  description?: string
  visibility?: string
  type: "folder" | "file" | "project"
  extension?: string
  mime_type?: string
  version?: number
  size?: number
  total_size?: number
  file_count?: number
  folder_count?: number
  /** Present (and truthy) when this "project" is a LaTeX project — see PinItem/CreateProjectData, which use the same field to distinguish LaTeX from a plain folder-type project. */
  compiler?: string
  output_type?: string
  is_deleted: boolean
  created_at: string
  updated_at: string
  role: "read" | "editor" | "owner"
  tags?: string[]
}


export interface SharedWithMeResponse {
  success: boolean
  message: string
  data: SharedResource[]
}

export interface FileTreeNode {
  id: string
  project_id: string
  parent_id?: string
  owner_id: string
  name: string
  type: "folder" | "file"
  extension?: string
  mime_type?: string
  project_type?: string
  compiler?: string
  description?: string
  thumbnail_key?: string
  output_type?: string
  version: number
  size: number
  is_pinned?: boolean
  is_deleted: boolean
  created_at: string
  updated_at: string
  file_count: number
  folder_count: number
  children?: FileTreeNode[]
}

export interface FileTreeResponse {
  success: boolean
  message: string
  data: FileTreeNode[]
}

export interface FilePreviewData {
  file_id: string
  name: string
  mime_type: string
  /** Signed storage URL returned by the backend (`url` or legacy `preview_url`). */
  preview_url: string
  url: string
  expires_in: number
}

export interface FilePreviewResponse {
  success: boolean
  message: string
  data: FilePreviewData
}

export interface TrashProject {
  id: string
  owner_id: string
  name: string
  description: string
  visibility: string
  type: string
  folder_count: number
  file_count: number
  total_size: number
  /** Present (and truthy) when this trashed project is a LaTeX project — same field as SharedResource/PinItem/CreateProjectData. Note GET /trash's own `type` is unreliable for this (can come back as ""), so this is the field to check instead. */
  compiler?: string
  output_type?: string
  is_deleted: boolean
  created_at: string
  updated_at: string
}

export interface TrashFile {
  id: string
  project_id: string
  parent_id?: string
  owner_id: string
  name: string
  type: "folder" | "file"
  extension?: string
  mime_type?: string
  version: number
  size: number
  is_deleted: boolean
  created_at: string
  updated_at: string
}

export interface TrashData {
  projects: TrashProject[]
  files: TrashFile[]
}

export interface TrashResponse {
  success: boolean
  message: string
  data: TrashData
}

export interface PinItem {
  id: string
  project_id?: string
  owner_id: string
  owner_name?: string
  name: string
  description?: string
  visibility?: string
  tags?: string[]
  type: "file" | "folder" | "project"
  extension?: string
  mime_type?: string
  version?: number
  size?: number
  total_size?: number
  folder_count?: number
  file_count?: number
  thumbnail_key?: string
  compiler?: string
  output_type?: string
  is_pinned?: boolean
  is_deleted: boolean
  created_at: string
  updated_at: string
}

export interface PinsResponse {
  success: boolean
  message: string
  data: {
    items: PinItem[]
  }
}

export interface WalletBalanceData {
  user_id: string
  balance: number
  lifetime_used: number
  lifetime_credited: number
  updated_at: string
}

export interface WalletBalanceResponse {
  success: boolean
  message: string
  data: WalletBalanceData
}

export interface InitUploadSessionRequest {
  file_name: string
  file_size: number
  mime_type: string
  file_hash?: string
  parent_id?: string
  project_id?: string
  /** The chunk size the frontend has decided to use and will actually slice with — sent on both
   *  session creation and every resume lookup, so the backend never has to guess/re-derive it and a
   *  resume can't drift from what was originally used. See computePartSize in lib/upload-session.ts. */
  part_size?: number
}

export interface InitUploadSessionResponse {
  success: boolean
  message: string
  data: {
    uploadSessionId: string
    file_id: string
    project_id: string
    version: number
    part_size: number
    total_parts: number
    status: string
  }
}

export interface UploadPartUrlRequest {
  uploadSessionId: string
  part_number: number
}

export interface UploadPartUrlResponse {
  success: boolean
  message: string
  data: {
    session_id: string
    part_number: number
    upload_url: string
  }
}

export interface CompleteUploadSessionRequest {
  uploadSessionId: string
  parts: { part_number: number; etag: string; size: number }[]
}

export interface CompleteUploadSessionResponse {
  success: boolean
  message: string
  data: {
    file_id: string
    project_id: string
    version: number
    size: number
    status: string
  }
}

/* The backend has been inconsistent about session_id vs uploadSessionId across endpoints —
   both are accepted here defensively; use uploadSessionIdOf() to read whichever is present. */
export interface UploadSessionStatusData {
  session_id?: string
  uploadSessionId?: string
  file_id: string
  project_id: string
  parent_id?: string
  file_name: string
  mime_type: string
  file_size: number
  part_size: number
  total_parts: number
  uploaded_parts: (number | { part_number: number; etag?: string; size?: number })[]
  uploaded_bytes: number
  /** The backend's own authoritative continuation point — 0 (or absent, on older responses) means
   *  every part it expects is already confirmed and there's nothing left to request; upload should
   *  go straight to completeUploadSession instead of trying to derive a next part from part sizes. */
  next_part_number?: number
  status: string
}

export interface VerifyUploadSessionHashRequest {
  uploadSessionId: string
  file_hash: string
}

export interface VerifyUploadSessionHashResponse {
  success: boolean
  message: string
  data: {
    valid: boolean
  }
}

export interface UploadSessionStatusResponse {
  success: boolean
  message: string
  data: UploadSessionStatusData
}

/* Lighter-weight than UploadSessionStatusData — the paused-list endpoint reports uploaded_parts
   as a count, not the full per-part array (that detail only comes from the single-session status
   / resume endpoints). Both session_id and uploadSessionId are accepted defensively, same as above. */
export interface PausedUploadSessionSummary {
  session_id?: string
  uploadSessionId?: string
  file_id: string
  project_id: string
  parent_id?: string
  file_name: string
  mime_type: string
  file_size: number
  file_hash?: string
  status: string
  chunk_size: number
  uploaded_bytes: number
  total_parts: number
  uploaded_parts: number
  updated_at_utc: string
}

export interface CreateDownloadSessionRequest {
  resource_id: string
}

export interface DownloadSessionEntry {
  file_id: string
  relative_path: string
  object_key?: string
  file_size: number
  /** Present on resume/status responses, not on a fresh create. */
  file_hash?: string
  first_part: number
  last_part: number
}

export interface CreateDownloadSessionResponse {
  success: boolean
  message: string
  data: {
    downloadSessionId: string
    resource_type: "file" | "folder" | "project"
    resource_name: string
    part_size: number
    total_parts: number
    total_files: number
    total_size: number
    status: string
    entries: DownloadSessionEntry[]
  }
}

export interface ResumeDownloadSessionRequest {
  resource_id: string
}

/** Shared shape of resumeDownloadSession's and getDownloadSessionStatus's responses — both report
 *  the same session snapshot. completed_parts may come back as bare numbers or richer records, same
 *  ambiguity as the upload session status endpoint (see normalizeUploadedParts). */
export interface DownloadSessionStatusData {
  downloadSessionId: string
  resource_id: string
  resource_type: "file" | "folder" | "project"
  resource_name: string
  part_size: number
  total_parts: number
  total_files: number
  total_size: number
  completed_parts: (number | { part_number: number; bytes_received?: number })[]
  downloaded_bytes: number
  /** 0 (or absent) means every part the backend expects is already confirmed — nothing left to fetch. */
  next_part_number?: number
  status: string
  entries: DownloadSessionEntry[]
}

export interface DownloadSessionStatusResponse {
  success: boolean
  message: string
  data: DownloadSessionStatusData
}

export interface DownloadPartUrlRequest {
  downloadSessionId: string
  part_number: number
}

export interface DownloadPartUrlResponse {
  success: boolean
  message: string
  data: {
    download_url: string
    file_path: string
    file_id: string
    range_start: number
    range_end: number
    part_size: number
  }
}

export interface CompleteDownloadPartRequest {
  downloadSessionId: string
  part_number: number
  bytes_received: number
}

export interface CompleteDownloadSessionRequest {
  downloadSessionId: string
}

export interface CreateFolderRequest {
  name: string
  project_id: string
  parent_id?: string
}

export interface CreateFolderResponse {
  success: boolean
  message: string
  data: {
    id: string
    name: string
    project_id: string
    parent_id: string
    type: string
  }
}

export interface RenameResourcePayload {
  name: string
  extension?: string
  mime_type?: string
}

export interface CreateTextFileRequest {
  content: string
  file_name: string
  project_id?: string
  parent_id?: string
}

export interface CreateTextFileResponse {
  success: boolean
  message: string
  data: {
    id: string
    name: string
    type: string
  }
}

export interface CreateFilePayload {
  name: string
  extension: string
  mime_type: string
  project_id?: string
  parent_id?: string
  size: number
}

export interface CheckDuplicateRequest {
  name: string
  extension?: string
  project_id?: string
  parent_id?: string
}

export interface CheckDuplicateResponse {
  success: boolean
  message: string
  data: {
    exists: boolean
    resource_id?: string
    resource_type?: string
  }
}

export interface CopyResourceRequest {
  /** Explorer copy sends keep_both so a duplicate is created beside the original. */
  conflict_policy?: "skip" | "replace" | "keep_both"
  resource_ids: string[]
  target_parent_id?: string | null
  target_project_id?: string
}

export interface CopyResourceResponse {
  success: boolean
  message: string
  data?: {
    conflictCount?: number
    created: string[] | null
    operationId?: string
    processed: number
    projectId?: string
    skipped: number
    status: string
  }
}

export type CopyConflictAction = "skip" | "replace" | "keep_both"

export interface CopyResolveRequest {
  defaultAction: CopyConflictAction
  overrides?: Array<{
    action: CopyConflictAction
    itemId: string
  }>
}

export interface MoveResourceRequest {
  conflict_policy: "skip" | "replace"
  parent_id?: string
  target_project_id?: string
}

export interface MoveResourceResponse {
  success: boolean
  message: string
  data?: {
    conflictCount: number
    moved: string
    name: string
    operationId: string
    status: string
  }
}

export interface MoveConflict {
  action: string
  created_at: string
  destination_name: string
  destination_parent_id: string
  error: string
  existing_destination_id: string
  id: string
  operation_id: string
  source_id: string
  source_name: string
  status: string
  updated_at: string
}

export interface MoveConflictsResponse {
  success: boolean
  message: string
  data: MoveConflict[]
}

export interface MoveResolveRequest {
  defaultAction: "SKIP" | "REPLACE" | "KEEP_BOTH"
  overrides?: Array<{
    action: string
    itemId: string
  }>
}

export interface MoveResolveResponse {
  success: boolean
  message: string
  data?: {
    conflictCount: number
    operationId: string
    status: string
  }
}

export interface UploadJobItem {
  /** Present on "upload"-type items. Downloads use download_session_id instead — use jobSessionId()
   *  from lib/use-upload-jobs.tsx to read whichever one applies. */
  uploadSessionId?: string
  /** Present on "download"-type items only. */
  download_session_id?: string
  file_id: string
  project_id: string
  file_name: string
  mime_type: string
  file_size: number
  file_hash?: string
  part_size: number
  total_parts: number
  completed_parts: number
  uploaded_bytes: number
  next_part_number: number
  status: string
  created_at_utc: string
  updated_at_utc: string
  /** Absent on older/upload-only responses — treat as "upload" when missing. */
  type?: "upload" | "download"
}

export interface UploadJobsResponse {
  success: boolean
  message: string
  data: {
    items: UploadJobItem[]
    page: number
    page_size: number
    total: number
  }
}

export interface UserPreferencesData {
  user_id: string
  mode: "system" | "light" | "dark" | "gray"
  syntax_highlighting: "light" | "dark" | "gray"
  diff_mode: "light" | "dark"
  color_removed_line: string
  color_added_line: string
  trash_retention_days: number
  show_whitespace: boolean
  tab_width: number
  use_relative_times: boolean
  file_time_format: "system" | "12h" | "24h"
  created_at: string
  updated_at: string
}

export interface UserPreferencesResponse {
  success: boolean
  message: string
  data: UserPreferencesData
}

export interface UpdateUserPreferencesRequest {
  mode?: "system" | "light" | "dark" | "gray"
  syntax_highlighting?: "light" | "dark" | "gray"
  diff_mode?: "light" | "dark"
  color_removed_line?: string
  color_added_line?: string
  trash_retention_days?: number
  show_whitespace?: boolean
  tab_width?: number
  use_relative_times?: boolean
  file_time_format?: "system" | "12h" | "24h"
}

export interface WalletHistoryItem{
  date: string
  action: string
  tokens: number
  remaining: number
  status: string
  expires_on?: string
}

export interface WalletHistoryData {
  items: WalletHistoryItem[]
  next_offset: number
  has_more: boolean
}

export interface WalletHistoryResponse {
  success: boolean
  message: string
  data: WalletHistoryData
}

export interface WalletHistoryParams {
  limit?: number
  offset?: number
}

export interface SearchFolderItem {
  id: string
  name: string
  owner_id?: string
  parent_id?: string
  project_id?: string
  type: "project" | "folder" | string
}

export interface SearchFoldersResponse {
  data: {
    items: SearchFolderItem[]
    page: number
    page_size: number
    total: number
  }
  message: string
  success: boolean
}

export const api = new ApiService()
